/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AutoCompleteComponent, FormArrayOf, StatesService, UIComponentState } from '@leandredev/common_ng_14';
import { interfaces } from '@leandredev/dcbm-common-lib';
import { BehaviorSubject, of } from 'rxjs';
import { debounceTime, map, startWith, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ActCreationComponent } from 'src/app/modules/acts/act-creation/act-creation.component';
import { IAct } from '@leandredev/dcbm-common-lib/dist/interfaces';
import { ApiService } from 'src/app/services/api/api.service';
import { ActsService } from 'src/app/services/acts/acts.service';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-act-form-auto-complete',
  templateUrl: './Act.component.html',
  styleUrls: ['./Act.component.scss']
})
export class ActFormAutoCompleteComponent extends AutoCompleteComponent<interfaces.IAct> implements OnInit, OnDestroy {
  private _selectTypesObs: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private _selectTypes: string[] = [];

  @Input()
  public set selectTypes(types: string[]) {
    if (types && types.length > 0) this._selectTypesObs.next(types);
  }

  private _enabledAdd = true;

  public get enabledAdd(): boolean {
    return this._enabledAdd;
  }

  @Input()
  public set enabledAdd(value: boolean) {
    this._enabledAdd = value;
  }

  @Input()
  public get hasOverridedData(): boolean {
    return this._hasOverridedData;
  }

  public set hasOverridedData(value: boolean) {
    this._hasOverridedData = value;

    if (!value) return;

    this.unsubscribePformCtrl();

    if (this.overridedData) this.filteredData = this.overridedData;
  }

  private _hasOverridedData = false;

  @Input()
  public get overridedData(): interfaces.IAct[] {
    return this._overridedData;
  }

  public set overridedData(value: interfaces.IAct[]) {
    this._overridedData = value;

    if (!value || !this.hasOverridedData) return;

    this.filteredData = value;
  }

  private _overridedData: interfaces.IAct[] = [];

  constructor(
    public uIComponentState: UIComponentState,
    public statesService: StatesService,
    private router: Router,
    private dialog: MatDialog,
    protected apiService: ApiService,
    private actService: ActsService
  ) {
    super(uIComponentState, statesService);

    this.httpServiceBase = apiService.collections.Act;

    if (this.hasOverridedData) return;

    this.pformCtrlSub = this._selectTypesObs
      .pipe(
        map((value) => (this._selectTypes = value)),
        switchMap(() => this._formCtrl.valueChanges),
        startWith(null),
        map((value) => {
          if (typeof value !== 'string') return '';
          return value.trim();
        }),
        map((name) => {
          let query = '';

          if (this._selectTypes && this._selectTypes.length > 0) {
            query += `type/${this._selectTypes.join('/')}/${this._selectTypes.length}/[/`;
          } else {
            query += '*/';
          }

          if (name.length > 0) {
            return query + `name/${name}/i/$regex/&/10/$limit/templateReference/$pop/`;
          } else {
            return query + `10/$limit/templateReference/$pop/`;
          }
        }),
        switchMap((query) => this.httpServiceBase.get(query)),
        map((result) => {
          if (!this.pformCtrlSub) return;

          if (this.hasOverridedData) {
            this.unsubscribePformCtrl();
            this.filteredData = this.overridedData;
            return;
          }

          this.filteredData = result.response as interfaces.IAct[];
        })
      )
      .subscribe({ error: (err) => console.error(err) });

    this.subscription.add(this.pformCtrlSub);
  }

  @Input()
  public override get formArr(): FormArrayOf<FormControl> {
    return this.pFormAr;
  }

  public override set formArr(value: FormArrayOf<FormControl>) {
    if (value === this.pFormAr) return;

    if (this.pFormArSub) {
      this.pFormArSub.unsubscribe();
      this.subscription.remove(this.pFormArSub);
      this.pFormArSub = null;
    }

    this.pFormAr = value;

    if (!this.pFormAr) return;

    this.pFormArSub = this.pFormAr.valueChanges
      .pipe(
        startWith(this.pFormAr.value),
        debounceTime(200),
        switchMap((ids: string[]) => {
          if (ids && ids.length <= 0) {
            this.populatedValue = [];
            return of(null);
          }

          this.populatedValue = this.populatedValue.filter((val) => ids.indexOf(val._id) > -1);

          const newIds: string[] = ids.filter((id) => this.populatedValue.find((elt) => elt._id === id) == null) || [];

          const currentPopulatedValue: interfaces.IAct[] = this.populatedValue.slice();

          if (!(newIds && newIds.length > 0)) return of(null);

          return this.httpServiceBase.get(`_id/${newIds.join('/')}/${newIds.length}/[/templateReference/$pop/`).pipe(
            map((dataRes) => {
              if (dataRes.code !== 200) return this.remapValueFrom(this.populatedValue, ids);

              return this.remapValueFrom(currentPopulatedValue.concat(dataRes.response), ids);
            }),
            map((value) => (this.populatedValue = value))
          );
        })
      )
      .subscribe();

    this.subscription.add(this.pFormArSub);
  }

  private remapValueFrom(arr: interfaces.IAct[], ids: string[]): interfaces.IAct[] {
    return ids.map((id) => {
      let res: interfaces.IAct = arr.find((elt) => elt._id === id);

      if (!res) res = { _id: id, name: 'error' } as IAct;

      return res;
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  private unsubscribePformCtrl(): void {
    if (this.pformCtrlSub) this.pformCtrlSub.unsubscribe();
  }

  public openActCreation(): void {
    let defaultTypeIds = [];

    if (this.formCtrl) defaultTypeIds = this.formCtrl.parent.value.type as string[];

    if (this.formArr) defaultTypeIds = this._selectTypes;

    const dialog = this.dialog.open(ActCreationComponent, { data: { defaultTypeIds: defaultTypeIds } });

    dialog
      .afterClosed()
      .pipe(
        map((act: IAct) => {
          if (!act) return;
          if (this.formCtrl) this.formCtrl.patchValue(act._id);
          if (this.formArr) this.formArr.patchValue(this.formArr.getRawValue().concat(act._id));
        })
      )
      .subscribe();
  }

  public editClassItem(actData: interfaces.IAct): void {
    this.actService
      .getActUrl(actData._id)
      .pipe(map((url) => void this.router.navigate([url])))
      .subscribe();
  }
}
